import Cookies from "js-cookie";
import LZString from "lz-string";
import { getAccountID } from "./AuthService";

export const DXGridCookieKeyTypes = {
    simpleManageShiftsGrid: "simple_manage_shifts_grid",
    doorsupervisionShiftsGrid: "security_shifts_grid",
    contractCleaningShiftGrid: "cleaning_shifts_grid",
    entertainmentShiftGrid: "entertainment_shifts_grid",
    eventUKManageShiftsGrid: "eventuk_manage_shifts_grid",
    rateMatrixGrid:"rate_matrix_grid",
    providerUploadMapping:'provider_upload_mapping',
    clientUploadMapping:'client_upload_mapping',
    paymentRequestGridMatched: "payment_req_grid",
    paymentRequestGridForm: "payment_req_grid_form",
    gigRequestGrid :"gig_req_grid",
    queryGrid: "queryGrid",
    youtubeGrid: "youtubeGrid",
};

interface CookieCacheDictionary {
    [key: string]: string;
}

const GRID_STATE_VERSION_PREFIX = "v2";
const GRID_LAYOUT_ACCOUNT_ID_CACHE_KEY = "dx_grid_layout_account_id";

/**
 * Resolves a stable account id for grid state storage, including early app
 * startup when auth state is still hydrating.
 */
const resolveGridStorageAccountId = (): string => {
    const accountId = getAccountID();

    if (accountId && accountId.length > 0) {
        try {
            localStorage.setItem(GRID_LAYOUT_ACCOUNT_ID_CACHE_KEY, accountId);
        } catch {
            // Ignore local storage write failures.
        }

        return accountId;
    }

    try {
        return localStorage.getItem(GRID_LAYOUT_ACCOUNT_ID_CACHE_KEY) || "";
    } catch {
        return "";
    }
};

/**
 * Builds a per-user key so grid layouts are isolated across signed-in users.
 */
export const getUserScopedDXGridStorageKey = (storageKey: string): string => {
    if (/_v2_[A-Za-z0-9_-]+$/.test(storageKey)) {
        return storageKey;
    }

    const accountId = resolveGridStorageAccountId();

    if (!accountId || accountId.length === 0) {
        return storageKey;
    }

    const safeAccountId = accountId.replace(/[^a-zA-Z0-9_-]/g, "_");
    return `${storageKey}_${GRID_STATE_VERSION_PREFIX}_${safeAccountId}`;
};

const CookieService = (function () {
    /** Initialize */
    var internalCookieCache: CookieCacheDictionary = {
        [DXGridCookieKeyTypes.eventUKManageShiftsGrid]: "",
        [DXGridCookieKeyTypes.simpleManageShiftsGrid]: "",
    };
    /**
     * js-cookie library auto encodes/decodes on set/get calls.
     * Our requirements demand compressing the string, so we need to override the default encoding/decoding
     */
    var cookiesWithNoDecoding = Cookies.withConverter({
        read: function (value: string, name: string): string {
            // decode override
            return value;
        },
        write: function (value: string | object, name: string): string {
            // encode override
            return value as string;
        },
    });
    /**
     * Save DevExtreme grid configurations.  Note: this is not per user.
     * Compression required as the Manage Shifts grid is too large to store uncompressed and it will grow with future requirements
     *
     * NOTE:  Using a Cookie will have the following issues
     *          1. Will be included on all API requests - impacting performace of the entire application.
     *          2. Has a 4kb storage limit, hence the need to compress any data being stored.  Uncompressing this data also has a performance impact.
     */
    const save = (storageKey: string, gridState: Object): void => {
        //var stateAsString = JSON.stringify(gridState);
        internalCookieCache[storageKey] = JSON.stringify(gridState);
        var compressedState = LZString.compressToUTF16(internalCookieCache[storageKey]);
        cookiesWithNoDecoding.set(storageKey, compressedState, {
            expires: 365,
            path: "/",
        });
    };
    /**
     * Load DevExtreme grid configurations.  Note: this is not per user.
     */
    const load = (storageKey: string): Promise<Object> => {
        var stateAsObject = null;

        if (internalCookieCache[storageKey].length > 0) {
            stateAsObject = JSON.parse(internalCookieCache[storageKey]);
        } else {
            var gridState: string = cookiesWithNoDecoding.get(storageKey) || "";

            if (gridState && gridState.length > 0) {
                var decompressedState = LZString.decompressFromUTF16(gridState) || "";
                stateAsObject = JSON.parse(decompressedState);
            }
        }

        if (stateAsObject) {
            return Promise.resolve(stateAsObject);
        } else {
            // Do not .reject() as it will only cause the grid to display an infinite loading spinner.
            // Sending back an empty object will force the grid use the default settings.
            return Promise.resolve({});
        }
    };
    
    /**
     * Legacy per-grid layout save. Intentionally a no-op: grid layouts are now
     * persisted per user by the global GridLayoutPersistence (DB API).
     *
     * Why: the DevExtreme React wrapper rolls back our runtime
     * `stateStoring` override to the JSX `<StateStoring customSave=...>` props,
     * so legacy saves would keep writing a second, competing layout store.
     */
    const localSave = (_storageKey: string, _gridState: Object): void => {
        // Superseded by GridLayoutPersistence.
    };

    
    /**
     * Legacy per-grid layout load. Always resolves `{}` so DevExtreme's native
     * stateStoring treats it as "no state" and never applies it.
     *
     * Why: the DevExtreme React wrapper re-enables the JSX `<StateStoring>`
     * after GridLayoutPersistence disables it, which triggers a native load.
     * Returning a stale legacy layout here overwrote the DB layout on every
     * visit (only when browser storage was NOT cleared). Must resolve, never
     * reject, or the grid shows an infinite loading spinner.
     */
    const localLoad = (_storageKey: string): Promise<Object> => {
        return Promise.resolve({});
    };

    return {
        saveDXGridConfiguration: localSave,
        loadDXGridConfiguration: localLoad
    };
})();

export default CookieService;
